import React, { useContext, useState } from "react";
import {
  WhmIconButton,
  WhmBox,
  WhmTypography,
  whmTheme,
} from "@securitize/reactjs-whm";
import { InfoCard } from "../../shared/InfoCard";
import { translationKeys } from "../../../../contexts/I18nContext";
import { Translate } from "../../../Translation/Translate";
import { SnackbarContext } from "../../../shared/Snackbar";
import { ReactComponent as CopyIcon } from "@securitize/assets-sec-ui/dist/icons/svg/copy.svg";

interface SettingsAccountIDCardProps {
  displayableId: string;
}

function formatAccountID(id: string): string {
  const digits = id.replace(/\D/g, "").substring(0, 10);
  if (digits.length !== 10) {
    return id;
  }
  return `${digits.substring(0, 3)}-${digits.substring(3, 7)}-${digits.substring(7, 10)}`;
}

export default function SettingsAccountIDCard({
  displayableId,
}: SettingsAccountIDCardProps) {
  const [isCopying, setIsCopying] = useState(false);
  const { showSnackbar } = useContext(SnackbarContext);

  const formattedID = formatAccountID(displayableId);

  const handleCopyToClipboard = async () => {
    setIsCopying(true);
    try {
      await navigator.clipboard.writeText(formattedID);
      showSnackbar({
        severity: "success",
        message: (
          <Translate
            translationKey={translationKeys.PROFILE_SETTINGS_ACCOUNT_ID_COPIED}
          />
        ),
      });
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    } finally {
      setIsCopying(false);
    }
  };

  return (
    <InfoCard
      title={
        <Translate
          translationKey={
            translationKeys.PROFILE_SETTINGS_ACCOUNT_ID_CARD_TITLE
          }
        />
      }
      subtitle={
        <Translate
          translationKey={
            translationKeys.PROFILE_SETTINGS_ACCOUNT_ID_CARD_SUBTITLE
          }
        />
      }
      smallPadding
    >
      <WhmBox gap="8px" display="flex" alignItems="center">
        <WhmTypography
          variant="h6"
          color={whmTheme.palette.text.primary}
          data-test-id="account-id-value"
        >
          {formattedID}
        </WhmTypography>

        <WhmIconButton
          onClick={() => void handleCopyToClipboard()}
          disabled={isCopying}
          data-test-id="account-id-copy-button"
          size="small"
        >
          <CopyIcon
            width={20}
            height={20}
            fill={whmTheme.palette.action.active}
          />
        </WhmIconButton>
      </WhmBox>
    </InfoCard>
  );
}
